import {
	Box,
	Button,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	tableCellClasses,
} from '@mui/material'
import { ReactComponent as AttentionIcon } from 'assets/attention.svg'
import { ReactComponent as ArrowNext } from 'assets/fill_back_arrow.svg'
import { ReactComponent as UndigitizedIcon } from 'assets/Undigitized.svg'
import { roundingNumber } from 'components/shared/processes'
import { getFatnessColorByTable } from 'components/cattles/fatnessPage/shared/fatnessScoreColor'
import { IFiltersLamenessTable } from 'models'
import { ICowsList } from 'models/ICattlesData'
import moment from 'moment'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { hideDigitizationUi } from 'utils/companyDigitizationFeatures'
import { showMilkYieldData } from 'utils/companyMilkFeatures'
import TableSkeleton from 'ui/skeleton/TableSkeleton'
import TooltipHover from 'ui/TooltipHover'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { TypographySecondaryBold16 } from 'ui/typography/TypographySecondaryBold16'
import { CattlesSortOrderings } from '../types/cattlesSortTypes'
import { CattlesTableHead } from './CattlesTableHead'
import { hasFarmCowNumber } from './cattleListFilters'

interface ICattlesTable {
	cattlesInfo: ICowsList[]
	fetchingCowsList: boolean
	params: IFiltersLamenessTable
	setParams: React.Dispatch<React.SetStateAction<IFiltersLamenessTable>>
	availableTags: { id: number; title: string }[]
	onChangeInput: (e: React.ChangeEvent<HTMLInputElement>) => void
	inputValue: string
	sortOrder: CattlesSortOrderings | null
	onSortOrder: (order: CattlesSortOrderings) => void
	isGroupPage?: boolean
}

const CattlesTable = (props: ICattlesTable) => {
	const {
		cattlesInfo,
		fetchingCowsList,
		sortOrder,
		onSortOrder,
		isGroupPage = false,
	} = props

	const { permissions } = useAppSelector(state => state.authReducer)
	const { userData } = useAppSelector(state => state.userReducer)
	const containerRef = useRef<HTMLDivElement | null>(null)
	const navigate = useNavigate()

	useEffect(() => {
		containerRef.current?.scrollTo({ top: 0, behavior: 'auto' })
	}, [cattlesInfo])
	const hasLactationData = cattlesInfo.some(
		cow => typeof cow.lactation_length === 'number' && cow.lactation_length > 0,
	)
	const canShowMilkYield = showMilkYieldData(
		userData?.company?.id,
		userData?.company?.dairy_integration,
	)
	const hideDigitization = hideDigitizationUi(userData?.company?.id)
	const showGroupColumn = !isGroupPage
	const showLactationColumn = isGroupPage || hasLactationData
	const showIdentificationColumns =
		permissions.license_identification && canShowMilkYield
	const visibleDataColumnsCount =
		1 +
		(showGroupColumn ? 1 : 0) +
		(showLactationColumn ? 1 : 0) +
		(showIdentificationColumns ? 2 : 0) +
		(permissions.license_fatness ? 1 : 0) +
		(permissions.license_limpness ? 1 : 0)
	const totalColumnsCount = visibleDataColumnsCount + 1
	const dataColumnWidth = `${(94 / visibleDataColumnsCount).toFixed(2)}%`

	const navigateCowPasportPage = (
		tagId: number | string,
		id: number,
		identified?: boolean,
	) => {
		navigate(`/cattles/cow/${encodeURIComponent(String(tagId))}`, {
			state: { cowId: id, identified },
		})
		window.scrollTo(0, 0)
	}

	return (
		<>
			{fetchingCowsList ? (
				<TableSkeleton
					widthForBody={'100%'}
					heightForBody={'34px'}
					rowCount={12}
					totalHeight={'calc(100vh - 350px)'}
				/>
			) : (
				<TableContainer
					ref={containerRef}
					sx={{
						overflowY: 'auto',
						overflowX: 'hidden',
						height: 'calc(100vh - 350px)',
						'&::-webkit-scrollbar': {
							width: 4,
						},
						'&::-webkit-scrollbar-track': {
							backgroundColor: '#DBDBDB',
							borderRadius: 12,
						},
						'&::-webkit-scrollbar-thumb': {
							backgroundColor: COLORS.icon,
							borderRadius: 12,
						},
					}}
				>
					<Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
						<CattlesTableHead
							sortOrder={sortOrder}
							onSortOrder={onSortOrder}
							showGroupColumn={showGroupColumn}
							showLactationColumn={showLactationColumn}
							showIdentificationColumns={showIdentificationColumns}
							showFatnessColumn={permissions.license_fatness}
							showLimpnessColumn={permissions.license_limpness}
							dataColumnWidth={dataColumnWidth}
						/>
						{cattlesInfo.length === 0 ? (
							<TableBody>
								<TableRow
									sx={{
										width: '100%',
										[`& .${tableCellClasses.root}`]: {
											borderBottom: 'none',
											height: '300px',
										},
									}}
								>
									<TableCell
										colSpan={totalColumnsCount}
										align={'center'}
										padding={'none'}
									>
										{isGroupPage ? (
											<TypographySecondaryBold16>
												Нет данных
											</TypographySecondaryBold16>
										) : (
											<Stack
												direction='column'
												justifyContent='center'
												alignItems='center'
												gap='8px'
											>
												<TypographySecondaryBold16>
													Нет данных
												</TypographySecondaryBold16>
												<TypographySecondary14>
													Нет данных, соответствующих выбранным условиям.
												</TypographySecondary14>
											</Stack>
										)}
									</TableCell>
								</TableRow>
							</TableBody>
						) : (
							<TableBody>
								{cattlesInfo.map((el, index) => {
									if (hasFarmCowNumber(el)) {
										return (
											<TableRow
												key={index}
												sx={{
													height: '34px',
													'&:hover': {
														background: COLORS.mainSecondaryOpacity,
													},
													[`& .${tableCellClasses.root}`]: {
														borderBottom: 'none',
													},
												}}
											>
												<TableCell align={'left'} padding={'none'}>
													<Box
														display={'flex'}
														flexDirection={'row'}
														alignItems={'center'}
														gap={'4px'}
													>
														<Box display={'flex'} flexDirection={'column'}>
															<Box display='flex' alignItems='center' gap='4px'>
																<TypographyPrimary
																	sx={{
																		whiteSpace: 'nowrap',
																		lineHeight: 1,
																		color:
																			!hideDigitization &&
																			el.identified === false
																				? COLORS.error
																				: COLORS.black,
																	}}
																>
																	{el.tag_id}
																</TypographyPrimary>

																{!hideDigitization &&
																	el.identified === false && (
																		<TooltipHover
																			title={
																				<span
																					style={{
																						color: COLORS.error,
																						fontWeight: 400,
																					}}
																				>
																					Корова не оцифрована.
																				</span>
																			}
																			placement='bottom'
																			oneLine
																			disableHoverBackground
																		>
																			<Box display='flex'>
																				<UndigitizedIcon
																					width={16}
																					height={16}
																				/>
																			</Box>
																		</TooltipHover>
																	)}
															</Box>
															{el.collar_num && (
																<TypographySecondary14
																	sx={{ whiteSpace: 'nowrap', lineHeight: 1 }}
																>
																	{el.collar_num}
																</TypographySecondary14>
															)}
														</Box>
													</Box>
												</TableCell>
												{showGroupColumn && (
													<TableCell align={'left'} padding={'none'}>
														<TypographyPrimary>
															{el.group.title}
														</TypographyPrimary>
													</TableCell>
												)}
												{showLactationColumn && (
													<TableCell align={'left'} padding={'none'}>
														{el.lactation_length ? (
															<TypographyPrimary>
																{el.lactation_length}{' '}
																<span
																	style={{
																		fontSize: '12px',
																		color: COLORS.secondary,
																	}}
																>
																	д
																</span>
															</TypographyPrimary>
														) : (
															<TypographySecondary14>
																Нет данных
															</TypographySecondary14>
														)}
													</TableCell>
												)}
												{showIdentificationColumns && (
													<>
														{userData.isIdentificationExpired ? (
															<>
																<TableCell align={'left'} padding={'none'}>
																	<TypographySecondary14>
																		Нет данных
																	</TypographySecondary14>
																</TableCell>
																<TableCell align={'left'} padding={'none'}>
																	<TypographySecondary14>
																		Нет данных
																	</TypographySecondary14>
																</TableCell>
															</>
														) : (
															<>
																<TableCell align={'left'} padding={'none'}>
																	{el.last_milking_amount ? (
																		<TypographyPrimary>
																			{roundingNumber(
																				el.last_milking_amount,
																				1,
																			)}{' '}
																			<span
																				style={{
																					fontSize: '12px',
																					color: COLORS.secondary,
																				}}
																			>
																				л
																			</span>
																		</TypographyPrimary>
																	) : (
																		<TypographySecondary14>
																			Нет данных
																		</TypographySecondary14>
																	)}
																</TableCell>
																<TableCell align={'left'} padding={'none'}>
																	<TypographySecondary14>
																		{el.last_milking_dt ? (
																			<>
																				{moment(el.last_milking_dt).format(
																					'DD.MM.YY',
																				)}
																				<br />
																				{moment(el.last_milking_dt).format(
																					'HH:mm:ss',
																				)}
																			</>
																		) : (
																			<TypographySecondary14>
																				Нет данных
																			</TypographySecondary14>
																		)}
																	</TypographySecondary14>
																</TableCell>
															</>
														)}
													</>
												)}
												{permissions.license_fatness && (
													<TableCell align={'left'} padding={'none'}>
														{userData.isFatnessExpired ? (
															<TypographySecondary14>
																Нет данных
															</TypographySecondary14>
														) : el.fatness == null ? (
															<TypographySecondary14>
																Нет данных
															</TypographySecondary14>
														) : (
															<TypographyPrimary
																sx={{
																	color: getFatnessColorByTable(
																		el.fatness,
																		el.lactation_length,
																		el.fatness_status,
																	),
																}}
															>
																{el.fatness}{' '}
																<span
																	style={{
																		fontSize: '12px',
																		color: COLORS.secondary,
																	}}
																>
																	б
																</span>
															</TypographyPrimary>
														)}
													</TableCell>
												)}
												{permissions.license_limpness && (
													<TableCell align={'left'} padding={'none'}>
														{userData.isLamenessExpired || !el.limpness ? (
															<TypographySecondary14>
																Нет данных
															</TypographySecondary14>
														) : (
															<TypographyPrimary>
																{el.limpness}{' '}
																<span
																	style={{
																		fontSize: '12px',
																		color: COLORS.secondary,
																	}}
																>
																	б
																</span>
															</TypographyPrimary>
														)}
													</TableCell>
												)}
												<TableCell align={'center'}>
													<TooltipHover
														title={'Перейти на страницу коровы'}
														placement={'bottom-start'}
														oneLine
													>
														<Button
															disableRipple={true}
															sx={{
																'&:hover': {
																	backgroundColor: 'transparent',
																},
															}}
															onClick={() =>
																navigateCowPasportPage(
																	el.tag_id,
																	el.id,
																	el.identified,
																)
															}
														>
															<ArrowNext
																style={{
																	cursor: 'pointer',
																	transform: 'rotate(180deg)',
																}}
																stroke={COLORS.secondary}
															/>
														</Button>
													</TooltipHover>
												</TableCell>
											</TableRow>
										)
									} else {
										return (
											<TableRow
												key={index}
												sx={{
													[`& .${tableCellClasses.root}`]: {
														borderBottom: 'none',
													},
													height: '34px',
													backgroundColor: COLORS.healthOpacity10,
												}}
											>
												<TableCell align={'left'} padding={'none'}>
													<Box
														display={'flex'}
														flexDirection={'row'}
														alignItems={'center'}
														gap={'4px'}
													>
														<AttentionIcon
															width={'16px'}
															height={'16px'}
															stroke={COLORS.health}
														/>
														<Box display={'flex'} flexDirection={'column'}>
															<Box display='flex' alignItems='center' gap='4px'>
																<TypographyPrimary
																	sx={{
																		whiteSpace: 'nowrap',
																		lineHeight: 1,
																		color:
																			!hideDigitization &&
																			el.identified === false
																				? COLORS.error
																				: COLORS.black,
																	}}
																>
																	{el.tag_id}
																</TypographyPrimary>
															</Box>
															{el.collar_num && (
																<TypographySecondary14
																	sx={{ whiteSpace: 'nowrap', lineHeight: 1 }}
																>
																	{el.collar_num}
																</TypographySecondary14>
															)}
														</Box>
													</Box>
												</TableCell>
												<TableCell
													align={'left'}
													padding={'none'}
													colSpan={totalColumnsCount - 1}
												>
													<TypographySecondary14>
														Корова не найдена в программе по надоям.
														Актуализируйте систему.
													</TypographySecondary14>
												</TableCell>
											</TableRow>
										)
									}
								})}
							</TableBody>
						)}
					</Table>
				</TableContainer>
			)}
		</>
	)
}

export default CattlesTable
